import { ExternalApiTarget } from '@/graphql/types';
import { useEffect } from 'react';
import { distinctUntilChanged, map, pairwise, startWith, withLatestFrom } from 'rxjs';
import { confirm } from '../../confirm/Confirm';
import { fromReactHookFormControl } from '../../reactForm/utils';
import type { SwarmFormController } from './form-controller';

function isNyDepTarget(value: unknown): boolean {
    return value === ExternalApiTarget.NewYorkApi;
}

function isNoneExternalApiTarget(value: unknown): boolean {
    return value === null || value === undefined || value === ExternalApiTarget.NoExternalApi;
}

const NY_DEP_CONFIRM_REQUIREMENTS = [
    gettext('NY_DEP_CONFIRM_REQUIREMENT_FREQUENCY_WEIGHTING_A'),
    gettext('NY_DEP_CONFIRM_REQUIREMENT_TIME_WEIGHTING_SLOW'),
    gettext('NY_DEP_CONFIRM_REQUIREMENT_LEQ_INTERVAL_5_MINUTES'),
    gettext('NY_DEP_CONFIRM_REQUIREMENT_LMAX_INTERVAL_5_MINUTES'),
    gettext('NY_DEP_CONFIRM_REQUIREMENT_MEASURING_SCHEDULE_24_HOURS'),
];

function getNyDepConfirmRequirements(isProject: boolean): string[] {
    if (isProject) {
        return NY_DEP_CONFIRM_REQUIREMENTS;
    }

    return [
        ...NY_DEP_CONFIRM_REQUIREMENTS,
        gettext('NY_DEP_CONFIRM_REQUIREMENT_MEASURING_POINT_LOCATION'),
    ];
}

function getNyDepConfirmMessage(isProject: boolean) {
    // The confirm dialog container centers text by default. This message has
    // several requirements, so render it as a normal left-aligned content block
    // instead of a centered blob of newline-delimited text.
    return (
        <div className="space-y-3 text-left">
            <p>{gettext('NY_DEP_CONFIRM_INTRO')}</p>
            <ul className="ml-5 list-disc space-y-1">
                {getNyDepConfirmRequirements(isProject).map((requirement) => (
                    <li key={requirement}>{requirement}</li>
                ))}
            </ul>
            <p>{gettext('NY_DEP_CONFIRM_CONSENT')}</p>
        </div>
    );
}

interface Props {
    formController: SwarmFormController;
}

/**
 * Shows a confirmation dialog when the user switches from "No External API" to the NY DEP
 * external API target. Cancelling reverts the selection.
 *
 * Lives outside ConfigurationWarning because this is a one-time transition
 * dialog, not a persistent state-based warning. It taps into the same value
 * stream used by the rest of the form system and guards against server-driven
 * changes with `serverValuesUpdate$`, matching the pattern used by
 * ConfigurationWarningsHelper.
 */
export function NyDepConfirmation({ formController }: Props) {
    useEffect(() => {
        const values$ = fromReactHookFormControl(formController.rhfProps.formControl, 'values');

        const subscription = values$
            .pipe(
                map((v) => (v as Record<string, unknown>).externalApiTarget),
                distinctUntilChanged(),
                startWith(undefined),
                pairwise(),
                withLatestFrom(formController.serverValuesUpdate$)
            )
            .subscribe(([[prev, current], serverUpdate]) => {
                // Ignore changes triggered by values coming from the server.
                if (serverUpdate) return;
                if (!isNoneExternalApiTarget(prev)) return;
                if (!isNyDepTarget(current)) return;

                confirm({
                    message: getNyDepConfirmMessage(formController.isProject),
                    forceRead: true,
                    onCancel() {
                        formController.rhfProps.formControl.setValue(
                            'externalApiTarget',
                            ExternalApiTarget.NoExternalApi
                        );
                    },
                });
            });

        return () => subscription.unsubscribe();
    }, [formController]);

    return null;
}
